/**
 * Carousel Slide 2 — "Info"
 * "Vad händer?" with 2–4 bullets derived from description.
 */

import React from 'react';
import { derivePreviewText } from '@/lib/text';
import type { ShareCardProps } from '../ShareCardBase';
import { FARMLY_COLORS, FARMLY_FONTS, FarmlyBadge } from '../ShareCardBase';

function deriveBullets(target: ShareCardProps['target']): string[] {
  let text = '';
  if (target.type === 'activity') {
    text = derivePreviewText(target.descriptionHtml, 400) || '';
  } else if (target.type === 'producer') {
    text = target.tagline || '';
  } else if (target.type === 'shop') {
    text = target.openingSnippet || '';
  } else if (target.type === 'product') {
    text = target.deliverySnippet || '';
  }

  if (!text) return ['Upplev något nytt', 'Lokalt och personligt', 'Nära naturen'];

  // Split on sentence boundaries, take up to 4
  const sentences = text
    .split(/[.!?\n]+/)
    .map((s) => s.trim())
    .filter((s) => s.length > 8 && s.length < 120);

  if (sentences.length === 0) return [text.slice(0, 100)];
  return sentences.slice(0, 4);
}

export function CarouselSlide2({ target, spec }: ShareCardProps) {
  const { w, h } = spec;
  const bullets = deriveBullets(target);
  const heading = target.type === 'activity' ? 'Vad händer?' : 'Mer om oss';

  return (
    <div style={{
      position: 'relative', width: w, height: h, overflow: 'hidden',
      background: FARMLY_COLORS.deepForest,
      display: 'flex', flexDirection: 'column', alignItems: 'flex-start',
      justifyContent: 'center', padding: 64,
      fontFamily: FARMLY_FONTS.body,
    }}>
      {/* Slide indicator */}
      <div style={{
        position: 'absolute', top: 40, right: 40, zIndex: 3,
        background: 'rgba(255,255,255,0.12)', borderRadius: 16,
        padding: '6px 16px', fontSize: 18, color: FARMLY_COLORS.warmSand,
        fontWeight: 600,
      }}>
        2 / 3
      </div>

      {/* Subtle accent circle */}
      <div style={{
        position: 'absolute', top: '20%', right: '-10%',
        width: w * 0.5, height: w * 0.5, borderRadius: '50%',
        background: 'rgba(255,255,255,0.03)',
      }} />

      {/* Producer */}
      <div style={{
        fontSize: 16, color: FARMLY_COLORS.warmSand, opacity: 0.6,
        textTransform: 'uppercase', letterSpacing: '0.12em',
        fontWeight: 500, marginBottom: 24,
      }}>
        {target.producerName}
      </div>

      {/* Heading */}
      <div style={{
        fontFamily: FARMLY_FONTS.display, fontSize: 44, lineHeight: 1.2,
        color: FARMLY_COLORS.warmSand, marginBottom: 40,
      }}>
        {heading}
      </div>

      {/* Bullets */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 20, maxWidth: w - 128 }}>
        {bullets.map((b, i) => (
          <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 16 }}>
            <div style={{
              width: 8, height: 8, borderRadius: 4,
              background: FARMLY_COLORS.accent,
              marginTop: 10, flexShrink: 0,
            }} />
            <div style={{
              fontSize: 24, lineHeight: 1.5, color: FARMLY_COLORS.warmSand,
              opacity: 0.85,
            }}>
              {b}
            </div>
          </div>
        ))}
      </div>

      {/* Decorative line */}
      <div style={{
        marginTop: 48, width: 60, height: 3,
        background: FARMLY_COLORS.accent, borderRadius: 2,
      }} />

      <FarmlyBadge style={{ bottom: 40 }} />
    </div>
  );
}
